import type { Side } from '@floating-ui/react';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import {
  cloneElement,
  type CSSProperties,
  memo,
  useId,
  useRef,
  useState,
} from 'react';

import { resolveTopLayerRoot } from '../../utils/resolveTopLayerRoot';
import './Tooltip.css';
import { resolveTooltipTriggerAs, type TooltipProps } from './types';

const ARROW_LENGTH = 8;

const mergeAriaDescribedBy = (
  existing: string | undefined,
  added: string,
): string => (existing ? `${existing} ${added}` : added);

export const Tooltip = memo(
  ({ children, direction = 'top', tooltip, __private }: TooltipProps) => {
    const { wrapTrigger = false } = __private ?? {};
    const Trigger = resolveTooltipTriggerAs(children.type);
    const [isOpen, setIsOpen] = useState(false);
    const tooltipId = useId();
    const arrowRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    // Resolved as the tooltip opens: the trigger is mounted by then, and its
    // host can change between openings (a dialog closing, a popover reused).
    const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();

    const {
      refs,
      context,
      floatingStyles,
      isPositioned,
      placement,
      middlewareData,
    } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        if (open) {
          setPortalRoot(resolveTopLayerRoot(triggerRef.current));
        }
        setIsOpen(open);
      },
      placement: direction,
      // A top-layer host clips the portalled tooltip when it hides its own
      // overflow; fixed positioning escapes that without leaving the layer.
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(ARROW_LENGTH + 6),
        flip({ fallbackAxisSideDirection: 'start' }),
        shift({ padding: 8 }),
        arrow({ element: arrowRef, padding: 4 }),
      ],
    });

    /*
     * `safePolygon` lets the cursor traverse the offset gap between the
     * trigger and the tooltip without dismissing it — required by WCAG
     * 1.4.13 (Content on Hover or Focus, "Hoverable"). `move: false`
     * suppresses the (rarely useful) pointermove-to-open behavior so a
     * cursor that is already over a tooltip trigger on mount doesn't open
     * it without a deliberate enter.
     */
    const hover = useHover(context, {
      move: false,
      handleClose: safePolygon(),
    });
    const focus = useFocus(context);
    const dismiss = useDismiss(context, {
      // Tooltips should only dismiss on Escape per the APG tooltip pattern;
      // outside-press dismissal would close the tooltip on every click on
      // the trigger itself, which already closes via mouse leave / blur.
      referencePress: false,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      focus,
      dismiss,
    ]);

    const triggerRefs = useMergeRefs([refs.setReference, triggerRef]);

    const [side, alignment] = placement.split('-') as [
      Side,
      'start' | 'end' | undefined,
    ];
    const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {};
    const isHorizontalArrow = side === 'top' || side === 'bottom';

    let arrowAxisStyle: CSSProperties | undefined = undefined;
    if (alignment === undefined && isHorizontalArrow && arrowX != null) {
      arrowAxisStyle = { left: `${arrowX}px` };
    } else if (
      alignment === undefined &&
      !isHorizontalArrow &&
      arrowY != null
    ) {
      arrowAxisStyle = { top: `${arrowY}px` };
    }

    /*
     * ARIA does not inherit, so `aria-describedby` must land on the
     * focused element itself — not on the wrapping reference span.
     * We can't move the ref onto the child instead because not every
     * focusable trigger forwards refs to a DOM node — EDS `Button`,
     * for example, doesn't.
     */
    const childWithDescribedBy = isOpen
      ? cloneElement(children, {
          'aria-describedby': mergeAriaDescribedBy(
            (children.props as { 'aria-describedby'?: string })[
              'aria-describedby'
            ],
            tooltipId,
          ),
        } as Partial<typeof children.props>)
      : children;

    if (!tooltip && !wrapTrigger) {
      return children;
    }

    const referenceProps = tooltip ? getReferenceProps() : undefined;

    return (
      <>
        <Trigger
          className="eds-tooltip--trigger"
          ref={triggerRefs}
          {...referenceProps}
        >
          {childWithDescribedBy}
        </Trigger>
        {tooltip && isOpen && (
          <FloatingPortal root={portalRoot}>
            <span
              className="eds-tooltip"
              data-placement={placement}
              data-side={side}
              id={tooltipId}
              ref={refs.setFloating}
              role="tooltip"
              style={{
                ...floatingStyles,
                visibility: isPositioned ? 'visible' : 'hidden',
              }}
              {...getFloatingProps()}
            >
              {tooltip}
              <span
                className="eds-tooltip--arrow"
                ref={arrowRef}
                style={arrowAxisStyle}
              />
            </span>
          </FloatingPortal>
        )}
      </>
    );
  },
);

Tooltip.displayName = 'Tooltip';
